import { useModuleBEM } from "@b2bportal/core-themes";
import { Icon } from "@tenant/ui";
import { OfferBanner } from "../OfferBanner";
import styles from "./BonusEarnBanner.module.scss";
import { BonusEarnDialog } from "./BonusEarnDialog";
import { useBonusEarnBanner } from "./useBonusEarnBanner";
import {
  ClaimStatus,
  HOTEL_3X_EARN_OFFER_NAME,
  IsOfferClaimed,
  OfferRawEventName,
  OfferScreen,
  OfferType,
  HOTEL_3X_EARN_OFFER_ID,
  useEmitRbcRawEvent,
  useEmitRbcRawEventOnce,
} from "@/tracking/offers";
import { TenantIconNameEnum } from "@/types/tenantIcons";
import { useIsEliteOrPremium } from "@/utilities/userTier";

export interface BonusEarnBannerProps {
  /** Screen the banner renders on — segments the banner impression/tap events. */
  screen?: OfferScreen;
}

export const BonusEarnBanner = ({
  screen = OfferScreen.home,
}: BonusEarnBannerProps) => {
  const [block] = useModuleBEM(styles, "BonusEarnBanner");
  const { context, handlers } = useBonusEarnBanner();
  const { resource, isDialogOpen, text, ctaLabel, ctaAriaLabel } = context;
  const { onViewClick, onDialogOpenChange } = handlers;

  const isOfferEligible = useIsEliteOrPremium();

  const emitTappedBanner = useEmitRbcRawEvent(
    OfferRawEventName.TappedOfferBanner
  );

  useEmitRbcRawEventOnce(
    {
      name: OfferRawEventName.ViewedOfferBanner,
      properties: {
        offer_type: OfferType.rbc_funded_points,
        offer_id: HOTEL_3X_EARN_OFFER_ID,
        offer_name: HOTEL_3X_EARN_OFFER_NAME,
        screen,
        is_offer_eligible: isOfferEligible,
        is_offer_claimed: IsOfferClaimed.not_applicable,
        is_claimable: false,
      },
    },
    resource != null
  );

  if (!resource) {
    return null;
  }

  const handleViewClick = () => {
    emitTappedBanner({
      properties: {
        offer_type: OfferType.rbc_funded_points,
        offer_id: HOTEL_3X_EARN_OFFER_ID,
        offer_name: HOTEL_3X_EARN_OFFER_NAME,
        screen,
        claim_status: ClaimStatus.not_applicable,
        is_offer_claimed: IsOfferClaimed.not_applicable,
        is_claimable: false,
      },
    });
    onViewClick();
  };

  return (
    <>
      <OfferBanner
        icon={<Icon iconName={TenantIconNameEnum.offersPercent} aria-hidden />}
        title={text}
        ctaLabel={ctaLabel}
        onClick={handleViewClick}
        ariaLabel={ctaAriaLabel}
        className={block}
      />
      <BonusEarnDialog
        open={isDialogOpen}
        onOpenChange={onDialogOpenChange}
        bonusPointsEarnRate={resource.bonusPointsEarnRate}
        validUntil={resource.validUntil}
        imageUrl={resource.imageUrl}
        screen={screen}
      />
    </>
  );
};
